import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, DollarSign, CreditCard, Calendar } from 'lucide-react';

const Financing = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Flexible Financing Options
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Make your electrical projects more affordable with our convenient financing plans. Get the work you need done now and pay over time with flexible payment options.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <a href="https://apply.ifinancecanada.com/24687" target="_blank" rel="noopener noreferrer">
                  Apply Now
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Finance Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Financing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't let budget concerns delay important electrical work. Our financing options help you get the services you need today.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <DollarSign className="h-8 w-8" />
                </div>
                <CardTitle>Affordable Monthly Payments</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Break down larger projects into manageable monthly payments that fit your budget.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <CreditCard className="h-8 w-8" />
                </div>
                <CardTitle>Quick & Easy Approval</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Fast application process with quick approval decisions so you can start your project right away.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-blue-900 text-white rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-8 w-8" />
                </div>
                <CardTitle>Flexible Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Choose payment terms that work for you with various financing options available.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Financing Plans
            </h2>
            <p className="text-xl text-gray-600">
              Competitive rates and flexible terms to suit your needs
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Consumer Financing Available</h3>
              <p className="text-lg text-gray-600 mb-6">
                We partner with trusted financial institutions to offer competitive financing options for residential and commercial electrical projects.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Competitive Interest Rates</h4>
                    <p className="text-gray-600">Affordable financing with competitive rates based on creditworthiness</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Flexible Payment Terms</h4>
                    <p className="text-gray-600">Choose from various payment terms to match your budget</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">No Prepayment Penalties</h4>
                    <p className="text-gray-600">Pay off your loan early without additional fees</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Quick Application Process</h4>
                    <p className="text-gray-600">Simple online or in-person application with fast approval</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Affordable Monthly Payments Chart */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">Affordable Monthly Payments</h3>
              <p className="text-center text-gray-600 mb-8">
                See how affordable your project can be with flexible payment terms
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-900 text-white">
                      <th className="p-4 text-left font-semibold border border-blue-800">Amount Financed</th>
                      <th className="p-4 text-center font-semibold border border-blue-800">12 Months</th>
                      <th className="p-4 text-center font-semibold border border-blue-800">24 Months</th>
                      <th className="p-4 text-center font-semibold border border-blue-800">36 Months</th>
                      <th className="p-4 text-center font-semibold border border-blue-800">48 Months</th>
                      <th className="p-4 text-center font-semibold border border-blue-800">60 Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$2,500</td>
                      <td className="p-4 text-center border border-gray-200">$219/mo</td>
                      <td className="p-4 text-center border border-gray-200">$115/mo</td>
                      <td className="p-4 text-center border border-gray-200">$81/mo</td>
                      <td className="p-4 text-center border border-gray-200">$64/mo</td>
                      <td className="p-4 text-center border border-gray-200">$55/mo</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$5,000</td>
                      <td className="p-4 text-center border border-gray-200">$437/mo</td>
                      <td className="p-4 text-center border border-gray-200">$230/mo</td>
                      <td className="p-4 text-center border border-gray-200">$162/mo</td>
                      <td className="p-4 text-center border border-gray-200">$128/mo</td>
                      <td className="p-4 text-center border border-gray-200">$109/mo</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$7,500</td>
                      <td className="p-4 text-center border border-gray-200">$656/mo</td>
                      <td className="p-4 text-center border border-gray-200">$345/mo</td>
                      <td className="p-4 text-center border border-gray-200">$243/mo</td>
                      <td className="p-4 text-center border border-gray-200">$192/mo</td>
                      <td className="p-4 text-center border border-gray-200">$164/mo</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$10,000</td>
                      <td className="p-4 text-center border border-gray-200">$875/mo</td>
                      <td className="p-4 text-center border border-gray-200">$460/mo</td>
                      <td className="p-4 text-center border border-gray-200">$324/mo</td>
                      <td className="p-4 text-center border border-gray-200">$256/mo</td>
                      <td className="p-4 text-center border border-gray-200">$218/mo</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$15,000</td>
                      <td className="p-4 text-center border border-gray-200">$1,312/mo</td>
                      <td className="p-4 text-center border border-gray-200">$690/mo</td>
                      <td className="p-4 text-center border border-gray-200">$486/mo</td>
                      <td className="p-4 text-center border border-gray-200">$384/mo</td>
                      <td className="p-4 text-center border border-gray-200">$327/mo</td>
                    </tr>
                    <tr className="bg-white hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$20,000</td>
                      <td className="p-4 text-center border border-gray-200">$1,750/mo</td>
                      <td className="p-4 text-center border border-gray-200">$920/mo</td>
                      <td className="p-4 text-center border border-gray-200">$648/mo</td>
                      <td className="p-4 text-center border border-gray-200">$512/mo</td>
                      <td className="p-4 text-center border border-gray-200">$436/mo</td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 font-semibold border border-gray-200">$25,000</td>
                      <td className="p-4 text-center border border-gray-200">$2,187/mo</td>
                      <td className="p-4 text-center border border-gray-200">$1,150/mo</td>
                      <td className="p-4 text-center border border-gray-200">$810/mo</td>
                      <td className="p-4 text-center border border-gray-200">$640/mo</td>
                      <td className="p-4 text-center border border-gray-200">$545/mo</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700 text-center">
                  <strong>Note:</strong> Monthly payment estimates are approximate and for illustration purposes only. 
                  Actual rates and terms are subject to credit approval. Annual Percentage Rate (APR) varies based on creditworthiness. 
                  Contact us or apply online for personalized financing options.
                </p>
              </div>
              
              <div className="mt-6 text-center">
                <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800">
                  <a 
                    href="https://apply.ifinancecanada.com/24687" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Apply Now for Your Custom Rate
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Can You Finance */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Can You Finance?
            </h2>
            <p className="text-xl text-gray-600">
              Financing is available for a wide range of electrical services
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Panel Upgrades</h3>
              <p className="text-gray-600">Electrical panel replacements and service upgrades</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generator Installation</h3>
              <p className="text-gray-600">Whole-house backup generator systems</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Home Renovations</h3>
              <p className="text-gray-600">Complete electrical work for home remodeling</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Commercial Projects</h3>
              <p className="text-gray-600">Business electrical installations and upgrades</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">EV Charging Stations</h3>
              <p className="text-gray-600">Electric vehicle charging equipment installation</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">And More!</h3>
              <p className="text-gray-600">Most electrical services and installations qualify</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Getting started with financing is easy
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Your Quote</h3>
              <p className="text-gray-600">
                Contact us for a free estimate on your electrical project
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Apply for Financing</h3>
              <p className="text-gray-600">
                Complete a simple application with our financing partner
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Approved</h3>
              <p className="text-gray-600">
                Receive quick approval and choose your payment terms
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold">4</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Start Your Project</h3>
              <p className="text-gray-600">
                We begin work right away while you enjoy easy monthly payments
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Contact us today to learn more about our financing options and get your project started
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-blue-900 hover:bg-blue-800 text-white">
              <a href="https://apply.ifinancecanada.com/24687" target="_blank" rel="noopener noreferrer">
                Apply for Financing
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Financing;
